import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  TrendingUp,
  Building2,
  Users,
  Shield,
  Scale,
  DollarSign,
  CheckCircle,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Eye,
  Hammer,
  FileText,
  Lock,
  Heart,
} from "lucide-react";

/* ── FAQ Item ── */
const FAQItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 bg-white">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-bold text-slate-900 pr-4">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-[#002B7F] flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
};

const steps = [
  {
    num: "1",
    title: "Primary Home Protection",
    desc: "No additional tax on your primary residence. Homeownership is fully protected. If you own a home and live in it — nothing changes for you.",
    highlight: "Your home is not affected.",
    color: "bg-emerald-600",
  },
  {
    num: "2",
    title: "Ownership Threshold",
    desc: "The progressive tax applies only after approximately 3 properties for single filers and 5-6 for married filers. This ensures small homeowners, small landlords, and families with modest rental properties are not affected.",
    highlight: "Small homeowners are protected.",
    color: "bg-[#002B7F]",
  },
  {
    num: "3",
    title: "Progressive Structure",
    desc: "Starts at 0.5% and increases with the number of properties and total portfolio value. Caps at 10%. The more properties accumulated beyond the threshold, the higher the rate.",
    highlight: "Scales with accumulation — not ownership.",
    color: "bg-[#D4AF37]",
  },
  {
    num: "4",
    title: "Why This Structure Works",
    points: [
      "Discourages bulk buying of residential homes",
      "Reduces speculative demand that inflates prices",
      "Shifts housing supply back toward owner-occupiers",
      "Keeps existing homeowners and small landlords unaffected",
    ],
    color: "bg-slate-800",
  },
];

const antiLoopholes = [
  {
    icon: Users,
    title: "Common Ownership Detection",
    desc: "Properties owned by related entities or individuals are treated as one combined portfolio",
  },
  {
    icon: Lock,
    title: "LLC Consolidation",
    desc: "LLCs cannot be used to split ownership artificially. Ownership through multiple entities is aggregated",
  },
  {
    icon: Eye,
    title: "Blind LLC Maximum Rate",
    desc: "Any property owned through a blind LLC that refuses disclosure faces the maximum tax rate automatically",
  },
  {
    icon: FileText,
    title: "Full Ownership Disclosure",
    desc: "All residential property owners must disclose beneficial ownership publicly",
  },
];

const faqs = [
  {
    question: "Will this crash housing prices?",
    answer:
      "No. This plan is designed to reduce artificial inflation — not collapse the market. It targets speculative bulk buying that drives prices above what families can afford. Existing homeowners will see their home values stabilize rather than be subject to boom-bust speculation cycles. The goal is sustainable, accessible pricing — not a crash.",
  },
  {
    question: "Will investors leave the market entirely?",
    answer:
      "Some large-scale speculators may reduce their footprint — and that's the point. But small investors, landlords with a few properties, and developers building new homes are all protected. The plan targets accumulation at scale, not ownership itself. Responsible investment in housing remains fully viable.",
  },
  {
    question: "What happens to local communities?",
    answer:
      "Communities benefit directly. When more homes are available to families who will actually live in them, neighborhoods become more stable, school enrollment stabilizes, local businesses see more consistent customers, and community investment increases. Owner-occupied neighborhoods are stronger neighborhoods.",
  },
  {
    question: "Why limit the number of properties someone can own?",
    answer:
      "This plan doesn't limit ownership — it creates a progressive cost structure. You can still own as many properties as you want. But the tax structure ensures that accumulating residential homes at scale carries a proportional cost, preventing the kind of bulk buying that prices families out of the market.",
  },
  {
    question: "Why tax ownership instead of income?",
    answer:
      "Income-based approaches don't address the core problem: speculative demand. When investors buy homes as assets, the act of ownership itself creates price pressure — regardless of the income those properties generate. A property-based progressive structure directly addresses the behavior that drives prices up.",
  },
  {
    question: "I own a rental property. Am I affected?",
    answer:
      "If you own fewer than 3 properties (single) or 5-6 (married), you are not affected at all. The threshold is specifically designed to protect small landlords and families who own modest rental properties. This plan targets large-scale accumulation, not small-scale ownership.",
  },
];

const HousingPage = () => {
  return (
    <div data-testid="housing-page" className="pt-16 md:pt-20">
      {/* ── HERO ── */}
      <section className="relative py-16 md:py-24 bg-slate-900">
        <div className="absolute inset-0 bg-[#002B7F]/90" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-4">
            Lowering Housing Costs
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-4 max-w-3xl">
            A Nevada Where Working Families Can Afford to Buy Homes Again
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mb-8">
            A clear, step-by-step plan to reduce housing speculation, lower
            artificial price inflation, and restore access to homeownership for
            Nevada families.
          </p>

          {/* 3 bullets */}
          <div className="space-y-3 mb-8">
            {[
              "Stops large-scale housing speculation",
              "Prioritizes homes for families, not portfolios",
              "Brings housing prices back toward real affordability",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                <span className="text-white/90 font-medium">{item}</span>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-4">
            <a
              href="#the-plan"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Read the Full Plan
            </a>
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Get Involved
            </Link>
          </div>

          {/* Video Overview */}
          <div className="mt-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-3">Watch the Overview</p>
            <div className="relative aspect-video bg-black border border-white/20 overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/FWIrBlWZCqI?rel=0&modestbranding=1"
                title="Lowering Housing Costs - Cooper for Nevada"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                data-testid="housing-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── THE PROBLEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
              What Is Happening Right Now
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Home prices are rising faster than wages across Nevada. But it's
              not just a supply problem — it's a demand problem. Large-scale
              investors are competing directly with families, buying entire
              neighborhoods as investment assets and pricing out the people who
              actually want to live there.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {[
                {
                  icon: TrendingUp,
                  title: "Prices Outpacing Wages",
                  desc: "Home prices rising significantly faster than what Nevada families earn",
                },
                {
                  icon: Building2,
                  title: "Investor Competition",
                  desc: "Large investors competing with families, often with all-cash offers",
                },
                {
                  icon: Users,
                  title: "Neighborhoods as Assets",
                  desc: "Entire communities being acquired as portfolio investments",
                },
                {
                  icon: DollarSign,
                  title: "Lost Purchasing Power",
                  desc: "First-time buyers priced out of markets they could afford years ago",
                },
              ].map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="flex items-start gap-3 p-4 bg-red-50 border border-red-200"
                  >
                    <Icon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-red-800 text-sm">
                        {item.title}
                      </p>
                      <p className="text-red-700 text-xs">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-[#002B7F] p-6">
              <p className="text-white font-bold">
                Key Reality: Housing is increasingly treated as an investment
                product instead of a place to live.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY THIS MATTERS ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                What This Does to Families
              </h2>
              <div className="space-y-4 mb-8">
                {[
                  {
                    icon: Home,
                    title: "Delayed Homeownership",
                    desc: "Young families who should be buying their first home are locked out for years — or permanently",
                  },
                  {
                    icon: Users,
                    title: "Generational Wealth Gap",
                    desc: "Families who can't buy homes can't build equity, widening the wealth gap for the next generation",
                  },
                  {
                    icon: Building2,
                    title: "Forced Long-Term Renting",
                    desc: "Families who want to own are trapped in a rental market that also keeps rising",
                  },
                  {
                    icon: Heart,
                    title: "Rising Instability",
                    desc: "Young households face constant uncertainty about where they'll live and what they'll pay",
                  },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="flex items-start gap-4 p-4 bg-white border border-slate-200"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white flex-shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-sm">
                          {item.title}
                        </p>
                        <p className="text-slate-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-[#002B7F] p-8">
              <p className="text-white/90 leading-relaxed mb-6 text-lg">
                The issue isn't that people don't work hard enough — it's that
                the system has become harder to access.
              </p>
              <p className="text-[#D4AF37] font-bold text-lg">
                Working families deserve a fair shot at homeownership. This plan
                gives them that shot.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CORE POLICY OVERVIEW ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
            The Structural Change
          </h2>
          <p className="text-slate-600 leading-relaxed mb-8 text-lg">
            Nevada will implement a progressive property tax system based on
            housing ownership scale and total value. This creates a proportional
            cost for accumulating residential properties at scale — reducing
            speculative demand and making homes more accessible for families.
          </p>
          <div className="bg-[#002B7F]/10 border border-[#002B7F]/20 p-6">
            <p className="text-[#002B7F] font-bold text-lg">
              Homes should primarily be owned to live in — not accumulated at
              scale as financial assets.
            </p>
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS — STEP BY STEP ── */}
      <section id="the-plan" className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              How It Works — Step by Step
            </h2>
          </div>

          <div className="space-y-6">
            {steps.map((step) => (
              <div
                key={step.num}
                className="bg-white border border-slate-200 p-6"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 flex-shrink-0 flex items-center justify-center ${step.color} text-white font-black text-lg`}
                  >
                    {step.num}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-3">
                      {step.title}
                    </h3>
                    {step.desc && (
                      <p className="text-slate-600 leading-relaxed mb-3">
                        {step.desc}
                      </p>
                    )}
                    {step.highlight && (
                      <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-200 px-4 py-2">
                        <CheckCircle className="w-4 h-4 text-emerald-600" />
                        <span className="text-emerald-800 font-bold text-sm">
                          {step.highlight}
                        </span>
                      </div>
                    )}
                    {step.points && (
                      <ul className="space-y-2">
                        {step.points.map((point) => (
                          <li
                            key={point}
                            className="flex items-start gap-2 text-sm"
                          >
                            <CheckCircle className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                            <span className="text-slate-700">{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY NOT JUST BUILD MORE? ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            "Why Not Just Build More?"
          </h2>
          <p className="text-white/80 leading-relaxed mb-6">
            Building more homes is important — and this plan encourages it. But
            supply alone does not solve the problem when speculative demand keeps
            outpacing it.
          </p>
          <div className="space-y-4">
            {[
              {
                title: "Building helps, but doesn't stop speculation",
                desc: "New homes enter the market and are immediately purchased by investors — not families",
              },
              {
                title: "Investors can outbid new supply",
                desc: "Cash offers from large portfolios consistently beat family buyers, regardless of how many homes are built",
              },
              {
                title: "Supply alone doesn't fix pricing pressure",
                desc: "As long as speculative demand exists, building more simply creates more investment inventory — not more affordable homes",
              },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white/10 border border-white/20 p-5"
              >
                <p className="font-bold text-white text-sm mb-1">
                  {item.title}
                </p>
                <p className="text-white/70 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-[#D4AF37] font-bold mt-8">
            You have to address both supply AND speculative demand. This plan
            does both.
          </p>
        </div>
      </section>

      {/* ── ANTI-LOOPHOLE SYSTEM ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Anti-Loophole System
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              No workarounds, no shell games. If you own the asset, you must be
              publicly accountable for it.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {antiLoopholes.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="bg-[#F8F9FA] border border-slate-200 p-6 hover:border-[#D4AF37] transition-colors"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#002B7F] text-white mb-4">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 mb-2 text-sm">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 text-xs">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── BUILDING PROTECTION ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
                Building Protection
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                This plan encourages construction, not punishes it. We want more
                homes built for families — not fewer.
              </p>
              <div className="space-y-3">
                {[
                  "New construction exempt temporarily during development phase",
                  "Development projects protected to encourage building",
                  "Builders and developers are encouraged, not punished",
                  "Incentives for building affordable family housing",
                ].map((item) => (
                  <div key={item} className="flex items-start gap-2">
                    <Hammer className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-[#002B7F] p-8">
              <p className="text-white/90 leading-relaxed mb-4">
                The goal of this plan is to increase the number of homes
                available to families — which means construction must continue
                and accelerate.
              </p>
              <p className="text-[#D4AF37] font-bold text-lg">
                We want more homes built for families — not fewer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── EXPECTED OUTCOME ── */}
      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-8">
            What Changes
          </h2>

          <div className="grid sm:grid-cols-2 gap-4 text-left mb-8">
            {[
              "Less investor competition for family homes",
              "More homes available on the market",
              "Downward pressure on artificial pricing",
              "Increased homeownership accessibility",
              "Stronger, more stable neighborhoods",
              "More new homes built for families",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 bg-white/10 p-4">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                <span className="text-white font-medium text-sm">{item}</span>
              </div>
            ))}
          </div>

          <div className="bg-white/10 border border-white/20 p-6">
            <p className="text-[#D4AF37] font-bold text-lg">
              This plan reduces artificial housing inflation so Nevada families
              can compete again.
            </p>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq) => (
              <FAQItem key={faq.question} question={faq.question} answer={faq.answer} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL SUMMARY + CTA ── */}
      <section className="py-16 md:py-24 bg-[#F8F9FA]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xl md:text-2xl font-bold text-slate-900 mb-8">
            This plan does not punish success. It corrects imbalance.
          </p>
          <p className="text-slate-600 leading-relaxed mb-8">
            Every family that works hard deserves a fair shot at owning a home.
            Not a rigged market. Not a bidding war against billion-dollar
            portfolios. A real chance — in the state they call home.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#002B7F]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6">
            Make Homeownership Possible Again
          </h2>
          <p className="text-white/70 mb-8">
            Join the movement to restore housing fairness in Nevada.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/volunteer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] hover:bg-[#B5952F] text-black font-bold uppercase tracking-widest text-sm transition-all hover:-translate-y-1 hover:shadow-xl"
            >
              Join the Movement
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white hover:bg-white hover:text-[#002B7F] font-bold uppercase tracking-widest text-sm transition-all"
            >
              Contact the Campaign
            </Link>
            <Link
              to="/policies/rental"
              className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/40 text-white/80 hover:border-white hover:text-white font-bold uppercase tracking-widest text-sm transition-all"
            >
              Lowering Rental Costs <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HousingPage;
